// src/pages/Users.js
import React, { useEffect, useState, useMemo } from "react";
import { getUsers, getUserById, updateUserByAdmin, deleteUserByAdmin } from "../../services/api";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  CircularProgress,
  Toolbar,
  InputBase,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Divider,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const ADMIN_MIN = 1000;
const ADMIN_MAX = 1100; // inclusive
const REGULAR_MIN = 11001;

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");

  // pagination — separate for each table
  const [pageAdmin, setPageAdmin] = useState(0);
  const [rowsPerPageAdmin, setRowsPerPageAdmin] = useState(10);
  const [pageRegular, setPageRegular] = useState(0);
  const [rowsPerPageRegular, setRowsPerPageRegular] = useState(10);

  // snackbar
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

  // edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editData, setEditData] = useState({
    id: null,
    name: "",
    email: "",
    phone: "",
    whatsapp: "",
    telegram: "",
    role: "",
    emailVerified: false,
    isKycVerified: false,
    password: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await getUsers();
      // support both patterns res.data.data or res.data
      const payload = res.data?.data || res.data || res || [];
      // if backend returned an object with numeric keys, try to normalize:
      if (!Array.isArray(payload) && typeof payload === "object") {
        const vals = Object.values(payload).filter(
          (v) =>
            v &&
            typeof v === "object" &&
            (v.email || v.name || v.rockstarId || v.id || v._id)
        );
        setUsers(vals);
      } else {
        setUsers(payload);
      }
    } catch (e) {
      console.error("Failed to load users", e);
      setSnack({ open: true, severity: "error", message: "Failed to load users" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  /* Filter + search */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = (u.name || u.fullName || "").toString().toLowerCase();
      const email = (u.email || "").toString().toLowerCase();
      const rockstarId = String(u.rockstarId || "").toLowerCase();
      const userId = String(u.id || u._id || "").toLowerCase();
      const phone = (u.phone || "").toString().toLowerCase();
      const emailVerified = (u.emailVerified ? "verified" : "unverified").toLowerCase();
      return (
        name.includes(q) ||
        email.includes(q) ||
        rockstarId.includes(q) ||
        userId.includes(q) ||
        phone.includes(q) ||
        emailVerified.includes(q)
      );
    });
  }, [users, query]);

  // helper to extract numeric portion of rockstarId (accepts strings like "RBT1102" or "1102")
  const extractRockstarNumber = (raw) => {
    const s = String(raw ?? "").trim();
    const m = s.match(/(\d+)/);
    return m ? Number(m[1]) : NaN;
  };

  // split filtered into admin reserved series and regular
  const adminSeries = useMemo(() => {
    return filtered.filter((u) => {
      const n = extractRockstarNumber(u.rockstarId);
      return !Number.isNaN(n) && n >= ADMIN_MIN && n <= ADMIN_MAX;
    });
  }, [filtered]);

  const regularSeries = useMemo(() => {
    return filtered.filter((u) => {
      const n = extractRockstarNumber(u.rockstarId);
      // treat anything >= REGULAR_MIN as regular; also include entries that didn't match admin range
      if (!Number.isNaN(n) && n >= REGULAR_MIN) return true;
      if (!Number.isNaN(n) && n >= ADMIN_MIN && n <= ADMIN_MAX) return false;
      // fall back: include everything not in admin range into regular
      return true;
    });
  }, [filtered]);

  const visibleAdmin = adminSeries.slice(pageAdmin * rowsPerPageAdmin, pageAdmin * rowsPerPageAdmin + rowsPerPageAdmin);
  const visibleRegular = regularSeries.slice(pageRegular * rowsPerPageRegular, pageRegular * rowsPerPageRegular + rowsPerPageRegular);

  // open edit dialog (fetch fresh user)
  const openEdit = async (e, id) => {
    e?.stopPropagation?.();
    setEditLoading(true);
    try {
      const res = await getUserById(id);
      const u = res.data?.data || res.data || res;
      setEditData({
        id: u.id,
        name: u.name || "",
        email: u.email || "",
        phone: u.phone || "",
        whatsapp: u.whatsapp || "",
        telegram: u.telegram || "",
        role: u.role || "",
        emailVerified: Boolean(u.emailVerified),
        isKycVerified: Boolean(u.isKycVerified),
        password: "",
      });
      setEditOpen(true);
    } catch (err) {
      console.error("Failed to fetch user", err);
      setSnack({ open: true, severity: "error", message: "Failed to load user" });
    } finally {
      setEditLoading(false);
    }
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditLoading(false);
    setEditData({
      id: null,
      name: "",
      email: "",
      phone: "",
      whatsapp: "",
      telegram: "",
      role: "",
      emailVerified: false,
      isKycVerified: false,
      password: "",
    });
  };

  const handleEditChange = (field, value) => {
    setEditData((s) => ({ ...s, [field]: value }));
  };

  const handleUpdate = async () => {
    // basic validation
    if (!editData.name.trim() || !editData.email.trim()) {
      setSnack({ open: true, severity: "error", message: "Name and email are required" });
      return;
    }

    setEditLoading(true);
    try {
      const payload = {
        name: editData.name,
        email: editData.email,
        phone: editData.phone,
        whatsapp: editData.whatsapp,
        telegram: editData.telegram,
        role: editData.role,
        emailVerified: editData.emailVerified,
        isKycVerified: editData.isKycVerified,
      };
      // include password only when supplied
      if (editData.password && editData.password.trim()) payload.password = editData.password.trim();

      await updateUserByAdmin(editData.id, payload);

      setSnack({ open: true, severity: "success", message: "User updated" });
      closeEdit();
      await load();
    } catch (err) {
      console.error("Update user error", err);
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Failed to update user";
      setSnack({ open: true, severity: "error", message: msg });
    } finally {
      setEditLoading(false);
    }
  };

 const handleDelete = async (e, id) => {
  e?.stopPropagation?.();

  // dynamic import ensures Swal exists at the moment we use it
  let Swal;
  try {
    Swal = (await import("sweetalert2")).default;
  } catch (impErr) {
    console.error("Failed to load sweetalert2", impErr);
    setSnack({ open: true, severity: "error", message: "Failed to load confirmation dialog" });
    return;
  }

  const result = await Swal.fire({
    title: "Are you sure?",
    text: "This action cannot be undone. The user will be permanently deleted.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes, delete",
    cancelButtonText: "Cancel",
    reverseButtons: true,
    focusCancel: true,
  });

  if (!result.isConfirmed) return;

  // show loading modal
  Swal.fire({
    title: "Deleting...",
    allowOutsideClick: false,
    didOpen: () => Swal.showLoading(),
  });

  setLoading(true);
  try {
    await deleteUserByAdmin(id);

    Swal.close();
    await Swal.fire({
      title: "Deleted",
      text: "User has been deleted successfully.",
      icon: "success",
      timer: 1200,
      showConfirmButton: false,
    });

    await load();
    setSnack({ open: true, severity: "success", message: "User deleted" });
  } catch (err) {
    console.error("Delete user error", err);
    Swal.close();
    await Swal.fire({
      title: "Error",
      text: err?.response?.data?.message || "Failed to delete user.",
      icon: "error",
    });
    setSnack({ open: true, severity: "error", message: "Failed to delete user" });
  } finally {
    setLoading(false);
  }
};

  const renderTable = (title, rows, visibleRows, page, rowsPerPage, onPageChange, onRowsPerPageChange, keyPrefix) => (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6">{title}</Typography>
        <Chip label={`${rows.length} user${rows.length === 1 ? "" : "s"}`} size="small" />
      </Box>
      <Divider />
      {loading ? (
        <Box sx={{ py: 10, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 60 }}>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell>Rockstar ID</TableCell>
                  <TableCell>Email Verified</TableCell>
                  <TableCell>KYC Verified</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No users found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRows.map((u, idx) => {
                    const index = page * rowsPerPage + idx + 1;
                    return (
                      <TableRow key={`${keyPrefix}-${u.id ?? u._id ?? idx}`} hover>
                        <TableCell>{index}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32 }}>{(u.name || u.fullName || "U").charAt(0).toUpperCase()}</Avatar>
                            <Typography sx={{ fontWeight: 600 }}>{u.name || u.fullName || "—"}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{u.email || "—"}</TableCell>
                        <TableCell>{u.phone || "—"}</TableCell>
                        <TableCell>{u.id ?? u._id ?? "—"}</TableCell>
                        <TableCell>{u.rockstarId || "—"}</TableCell>
                        <TableCell>
                          <Chip label={u.emailVerified ? "Verified" : "Unverified"} color={u.emailVerified ? "success" : "warning"} size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip label={u.isKycVerified || u.isVerified ? "Verified" : "Unverified"} color={u.isKycVerified || u.isVerified ? "success" : "warning"} size="small" />
                        </TableCell>

                        <TableCell align="right">
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={(e) => openEdit(e, u.id)} type="button">
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={(e) => handleDelete(e, u.id)} type="button">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            onPageChange={(_, newPage) => onPageChange(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              onRowsPerPageChange(parseInt(e.target.value, 10));
              onPageChange(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
            showFirstButton
            showLastButton
          />
        </>
      )}
    </Paper>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Users
      </Typography>

      {/* Search + refresh bar */}
      <Paper sx={{ mb: 2, p: 1 }}>
        <Toolbar sx={{ gap: 2, display: "flex", flexWrap: "wrap" }}>
          <Paper
            component="form"
            sx={{
              p: "2px 8px",
              display: "flex",
              alignItems: "center",
              width: 360,
              borderRadius: 2,
              boxShadow: "none",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
            onSubmit={(e) => e.preventDefault()}
          >
            <SearchIcon color="action" />
            <InputBase sx={{ ml: 1, flex: 1 }} placeholder="Search name, email, phone, ID..." value={query} onChange={(e) => { setQuery(e.target.value); setPageAdmin(0); setPageRegular(0); }} />
            {query && (
              <IconButton size="small" onClick={() => setQuery("")}>
                ✕
              </IconButton>
            )}
          </Paper>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Refresh">
            <IconButton onClick={load}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </Paper>

      <Stack spacing={3}>
        {renderTable(`Admin Reserved IDs (${ADMIN_MIN}–${ADMIN_MAX})`, adminSeries, visibleAdmin, pageAdmin, rowsPerPageAdmin, setPageAdmin, setRowsPerPageAdmin, "admin")}
        {renderTable(`Regular IDs (${REGULAR_MIN}+ )`, regularSeries, visibleRegular, pageRegular, rowsPerPageRegular, setPageRegular, setRowsPerPageRegular, "regular")}
      </Stack>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent dividers>
          <Box display="grid" gap={2} pt={1}>
            <TextField label="Name" value={editData.name} onChange={(e) => handleEditChange("name", e.target.value)} fullWidth disabled={editLoading} />
            <TextField label="Email" value={editData.email} onChange={(e) => handleEditChange("email", e.target.value)} fullWidth disabled={editLoading} />
            <TextField label="Phone" value={editData.phone} onChange={(e) => handleEditChange("phone", e.target.value)} fullWidth disabled={editLoading} />
            <TextField label="WhatsApp" value={editData.whatsapp} onChange={(e) => handleEditChange("whatsapp", e.target.value)} fullWidth disabled={editLoading} />
            <TextField label="Telegram" value={editData.telegram} onChange={(e) => handleEditChange("telegram", e.target.value)} fullWidth disabled={editLoading} />

            <FormControl fullWidth>
              <InputLabel id="role-label">Role</InputLabel>
              <Select labelId="role-label" value={editData.role} label="Role" onChange={(e) => handleEditChange("role", e.target.value)} disabled={editLoading}>
                <MenuItem value={null}>—</MenuItem>
                <MenuItem value="CUSTOMER">Customer</MenuItem>
                <MenuItem value="ADMIN">Admin</MenuItem>
                <MenuItem value="MODERATOR">Moderator</MenuItem>
              </Select>
            </FormControl>

            <TextField label="Password (leave blank to keep)" type="password" value={editData.password} onChange={(e) => handleEditChange("password", e.target.value)} fullWidth disabled={editLoading} />

            <FormControlLabel control={<Switch checked={editData.emailVerified} onChange={(e) => handleEditChange("emailVerified", e.target.checked)} disabled={editLoading} />} label="Email Verified" />
            <FormControlLabel control={<Switch checked={editData.isKycVerified} onChange={(e) => handleEditChange("isKycVerified", e.target.checked)} disabled={editLoading} />} label="KYC Verified" />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeEdit} disabled={editLoading} type="button">
            Cancel
          </Button>
          <Button onClick={handleUpdate} variant="contained" disabled={editLoading} type="button">
            {editLoading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
